/**
 * A character's reaction to the player's choice, in a scripted chat (CCT-257).
 *
 * Pulled out of `MissionChatScreen` so it can be PROVEN: the rule is entirely textual, it needs
 * neither React nor navigation, and it is exactly the kind of logic whose defects only show up
 * once you play the scene to the end.
 */
import type { MessageKey } from '../i18n';

/**
 * What this util needs from i18n — nothing more. TYPE-ONLY import, erased at compile time, so the
 * module stays a LEAF and the tests can keep importing it directly to pin all four branches.
 */
type Translate = (key: MessageKey, values?: Record<string, unknown>) => string;

export interface QuestionJouee {
  type?: string;
  /** Texte commun à tous les choix (conclusion de scène) ou débrief unique. */
  explanation?: string;
  correctAnswer?: string | string[];
  /** Réaction écrite POUR chaque option, indexée par son libellé. */
  optionResponses?: Record<string, string>;
}

/**
 * Trois cas, dans cet ordre :
 *
 * 1. **Une réponse écrite pour le choix fait** → on la joue SEULE. Elle est déjà dans la voix du
 *    personnage et dit elle-même si c'est juste (« Exactement… », « Non. Une VM strongSwan… »).
 *    Le texte commun ne s'ajoute que s'il apporte autre chose — sinon on affichait deux fois le
 *    même paragraphe, et seulement sur les questions sans conclusion de scène : un doublon
 *    intermittent, donc difficile à attribuer.
 * 2. **Réponse juste, explication unique** → l'explication EST la réplique du personnage : elle
 *    enchaîne sur le choix (« Et ce n'est pas une lecture, c'est l'état du dossier… »). La faire
 *    précéder de « Correct ! » la coupait de sa phrase, et doublonnait avec la coche déjà
 *    affichée sur l'option retenue.
 * 3. **Réponse fausse** → là, et là seulement, on nomme la bonne réponse : le joueur a tranché,
 *    la lui cacher ne lui apprend rien.
 */
export function buildFeedback(
  isCorrect: boolean | undefined,
  q: QuestionJouee,
  answer: string | string[] | undefined,
  t: Translate,
): string {
  const choisi = Array.isArray(answer) ? answer[0] : answer;
  const propre = choisi ? (q.optionResponses?.[choisi] ?? '').trim() : '';
  const commun = (q.explanation ?? '').trim();

  if (propre) {
    return commun && commun !== propre ? `${propre}\n\n${commun}` : propre;
  }
  // ⚠️ UN CHOIX DE DIALOGUE N'EST NI JUSTE NI FAUX — `isCorrect` y vaut `undefined`. Le joueur a
  // choisi ce qu'il DEMANDE : ni « Correct ! », ni « La bonne réponse était… », qui nommerait une
  // bonne réponse qu'aucun auteur n'a déclarée. Sans réplique écrite pour l'option retenue, il
  // reste le texte commun, et rien d'autre.
  if (isCorrect === undefined) {
    return commun;
  }
  if (q.type === 'free-text') {
    return `${t('chat.explanation')}\n${commun || t('chat.thanks')}`;
  }
  if (isCorrect) {
    return commun || t('chat.correct');
  }
  const correct = Array.isArray(q.correctAnswer)
    ? q.correctAnswer.join(', ')
    : (q.correctAnswer ?? '');
  const miss = t('chat.notQuite', { answer: correct });
  return commun ? `${miss}\n\n${commun}` : miss;
}
